import type {
  SignCoordinates,
  UpdateSignCoordinates,
} from "@/app/views/PdfViewer/domain/abstract.sign-object.domain.ts";
import { type Nullable } from "@pasta/box-v2";
import { defineStore } from "pinia";
import {
  type CreateSignaturePositionDto,
  type SignaturePositionDto,
  type UpdateSignaturePositionDto,
} from "../dto/signature-position.dto";
import { useSignaturePositionApi } from "../http/signature-position.api";

export interface RawSignaturePositionStoreState {
  currentDocumentId: Nullable<number>;
  currentLetterFileId: Nullable<number>;
  map: Map<number, SignCoordinates>;
}

export const useRawSignaturePositionStore = defineStore("raw-signature-position", {
  state: (): RawSignaturePositionStoreState => ({
    currentDocumentId: null,
    currentLetterFileId: null,
    map: new Map<number, SignCoordinates>(),
  }),
  actions: {
    setCurrent(docId: number | null, lfId: number | null) {
      this.currentDocumentId = docId;
      this.currentLetterFileId = lfId;
    },
    async fetchCurrentDoc() {
      if (this.currentDocumentId && this.currentLetterFileId) {
        const dtos = await useSignaturePositionApi().getMany(this.currentLetterFileId, this.currentDocumentId);
        this.map.clear();
        dtos.forEach(dto => {
          this.map.set(dto.id, dtoToSignCoordinates(dto));
        });
      }
    },
    async create(data: SignCoordinates) {
      const dto = await useSignaturePositionApi().create(
        this.currentLetterFileId!,
        this.currentDocumentId!,
        createDtoFromCoordinates(data, this.currentDocumentId!)
      );
      this.map.set(dto.id, data);
      return dto;
    },
    async update(id: number, data: UpdateSignCoordinates) {
      await useSignaturePositionApi().update(
        id,
        this.currentLetterFileId!,
        this.currentDocumentId!,
        updateDtoFromCoordinates(data)
      );
    },
    async delete(id: number) {
      await useSignaturePositionApi().delete(id, this.currentLetterFileId!, this.currentDocumentId!);
      this.map.delete(id);
    },
  },
  getters: {
    list(): SignCoordinates[] {
      return Array.from(this.map.values());
    },
  },
});

function dtoToSignCoordinates(dto: SignaturePositionDto): SignCoordinates {
  return { upperLeftPoint: { x: dto.x, y: dto.y }, width: dto.width, height: dto.height, pageIndex: dto.page };
}

function createDtoFromCoordinates(
  { upperLeftPoint, pageIndex, ...coordinates }: SignCoordinates,
  documentId: number
): CreateSignaturePositionDto {
  return { x: upperLeftPoint.x, y: upperLeftPoint.y, page: pageIndex, ...coordinates, document: documentId };
}

function updateDtoFromCoordinates({
  upperLeftPoint,
  ...coordinates
}: UpdateSignCoordinates): UpdateSignaturePositionDto {
  return { x: upperLeftPoint.x, y: upperLeftPoint.y, ...coordinates };
}
